/**
 * Arkus override of Volto 19.4.1 ContentMetadataTags (same title/canonical/description/robots logic),
 * extended with complete Open Graph + Twitter card tags:
 *  - og:image = opengraph_image > preview_image > image > preview_image_link, scale ~1200 px
 *    ("great"), else the branded fallback /arkus-share.png (1200x630, volto-arkus/public);
 *    VLT sets contentMetadataTagsImageField = 'preview_image', which our posts do not use, so
 *    Volto's own tag never rendered an image.
 *  - og:image:width/height/alt, og:type (article for News Item / Event / Success Story, else
 *    website), og:site_name "Arkus", article:published_time, twitter:title/description/image.
 * All URLs go through toPublicURL, i.e. the request host (seamless mode), never backend:8080.
 */
import React, { useEffect } from 'react';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import {
  toPublicURL,
  getBaseUrl,
  flattenToAppURL,
} from '@plone/volto/helpers/Url/Url';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';
import { getNavroot } from '@plone/volto/actions/navroot/navroot';
import config from '@plone/volto/registry';
import { useDispatch, useSelector } from 'react-redux';

const SITE_NAME = 'Arkus';
const ARTICLE_TYPES = ['News Item', 'Event', 'success_story'];
const SCALES = ['great', 'huge', 'larger', 'large', 'teaser', 'preview'];
export const FALLBACK_IMAGE = {
  url: '/arkus-share.png',
  width: 1200,
  height: 630,
  alt: 'Arkus - Delivering custom Salesforce solutions since 2010',
};

const fromImageField = (img) => {
  if (!img) return null;
  const scales = img.scales || {};
  for (const s of SCALES) {
    if (scales[s]?.download) {
      return { url: scales[s].download, width: scales[s].width, height: scales[s].height };
    }
  }
  return img.download ? { url: img.download, width: img.width, height: img.height } : null;
};

// relation fields (preview_image_link) are serialized with catalog-style image_scales
const fromRelation = (rel) => {
  const field = rel?.image_field;
  const info = field && rel?.image_scales?.[field]?.[0];
  if (!info) return null;
  const base = flattenToAppURL(rel['@id']);
  const scales = info.scales || {};
  for (const s of SCALES) {
    if (scales[s]?.download) {
      return { url: `${base}/${scales[s].download}`, width: scales[s].width, height: scales[s].height };
    }
  }
  return info.download ? { url: `${base}/${info.download}`, width: info.width, height: info.height } : null;
};

export const getShareImage = (content) =>
  fromImageField(content.opengraph_image) ||
  fromImageField(content.preview_image) ||
  fromImageField(content.image) ||
  fromRelation(content.preview_image_link) ||
  null;

const ContentMetadataTags = (props) => {
  const {
    opengraph_title,
    opengraph_description,
    seo_title,
    seo_description,
    seo_canonical_url,
    seo_noindex,
    title,
    description,
    effective,
    modified,
  } = props.content;
  const type = props.content['@type'];

  const dispatch = useDispatch();
  const pathname = useSelector((state) => state.router.location.pathname);
  const navroot = useSelector((state) => state.navroot?.data?.navroot);
  const site = useSelector((state) => state.site?.data);

  useEffect(() => {
    if (pathname && !hasApiExpander('navroot', getBaseUrl(pathname))) {
      dispatch(getNavroot(getBaseUrl(pathname)));
    }
  }, [dispatch, pathname]);

  const getTitle = () => {
    const includeSiteTitle =
      config?.settings?.siteTitleFormat?.includeSiteTitle || false;
    const titleAndSiteTitleSeparator =
      config?.settings?.titleAndSiteTitleSeparator || '-';
    const navRootTitle = navroot?.title;
    const siteRootTitle = site?.['plone.site_title'];
    const titlePart = navRootTitle || siteRootTitle;

    if (includeSiteTitle && titlePart && titlePart !== title) {
      return seo_title || `${title} ${titleAndSiteTitleSeparator} ${titlePart}`;
    } else {
      return seo_title || title;
    }
  };

  const contentImage = getShareImage(props.content);
  const image = contentImage
    ? { ...contentImage, alt: title || SITE_NAME }
    : FALLBACK_IMAGE;
  const imageURL = toPublicURL(flattenToAppURL(image.url));
  const url = seo_canonical_url || toPublicURL(props.content['@id']);
  const ogTitle = (opengraph_title || seo_title || title || SITE_NAME).replace(/\u00AD/g, '');
  const ogDescription = opengraph_description || seo_description || description;
  const isArticle = ARTICLE_TYPES.includes(type);

  return (
    <>
      <Helmet>
        <title>{getTitle()?.replace(/\u00AD/g, '')}</title>
        <link rel="canonical" href={url} />
        <meta name="description" content={seo_description || description} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:type" content={isArticle ? 'article' : 'website'} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:url" content={url} />
        {seo_noindex && <meta name="robots" content="noindex" />}
        <meta property="og:image" content={imageURL} />
        {imageURL.startsWith('https:') && (
          <meta property="og:image:secure_url" content={imageURL} />
        )}
        {image.width && <meta property="og:image:width" content={image.width} />}
        {image.height && <meta property="og:image:height" content={image.height} />}
        <meta property="og:image:alt" content={image.alt} />
        {ogDescription && <meta property="og:description" content={ogDescription} />}
        {isArticle && effective && effective > '1970' && (
          <meta property="article:published_time" content={effective} />
        )}
        {isArticle && modified && (
          <meta property="article:modified_time" content={modified} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        {ogDescription && <meta name="twitter:description" content={ogDescription} />}
        <meta name="twitter:image" content={imageURL} />
        <meta name="twitter:image:alt" content={image.alt} />
      </Helmet>
    </>
  );
};

export default ContentMetadataTags;
